import i18n from '../i18n';

/**
 * 워크플로 설정 — 캔버스 문서(CANVAS_CN)의 `settings` 로 함께 저장되고 Deploy 시 백엔드로 전달되어 DAG 인자가 된다.
 *   schedule: null(수동 실행만) | cron 문자열 | Airflow 프리셋(@hourly …)
 *   retries/retryDelayMin/timeoutMin → default_args(retries, retry_delay, execution_timeout)
 *   notify → on_failure/on_success 콜백(웹훅 POST, 이메일은 Airflow SMTP 설정 필요)
 */
export interface WorkflowSettings {
  schedule: string | null;
  startDate: string;          // YYYY-MM-DD
  catchup: boolean;
  retries: number;
  retryDelayMin: number;
  timeoutMin: number | null;  // null = 제한 없음
  notify: {
    onFailure: boolean;
    onSuccess: boolean;
    emails: string;           // 콤마 구분
    webhook: string;          // Slack/Teams/사내 웹훅 URL — {"text": "..."} JSON POST
  };
  /** 중간 데이터(내부데이터·태스크 결과) 전달 방식 — XCom 대신 파일/테이블 스테이징 (대량 처리) */
  staging: {
    mode: 'auto' | 'file' | 'table' | 'xcom';
    thresholdRows: number;    // auto: 이 행 수를 넘으면 파일로
    connection: string;       // table 모드의 커넥션(PostgreSQL)
    keepHours: number;        // 스테이징 파일 보존 시간
  };
  /** 워크플로 변수 — 이 워크플로 안 어디서나 ${이름} (전역변수 < 워크플로 변수 < 서브플로 < 노드 < 실행 값).
   *  서버 canvas_converter._scope_params 가 가장 바깥 범위로 싣는다 */
  params?: { name: string; value: string }[];
  /** SQL실행 노드의 SQL 끝에 식별 주석을 붙인다 — 비우면 켬. 끄면 노드 설정과 무관하게 붙이지 않는다 (서버 task_functions.tag_statement) */
  sqlComment?: boolean;
}

export const DEFAULT_WORKFLOW_SETTINGS: WorkflowSettings = {
  schedule: null,
  startDate: '2024-01-01',
  catchup: false,
  retries: 0,
  retryDelayMin: 5,
  timeoutMin: null,
  notify: { onFailure: false, onSuccess: false, emails: '', webhook: '' },
  staging: { mode: 'auto', thresholdRows: 10000, connection: '', keepHours: 24 },
};

/** labelKey 는 dialog 네임스페이스 키 — 모듈 스코프라 문구가 아니라 키를 둔다 */
export const SCHEDULE_PRESETS: Array<{ labelKey: string; value: string | null }> = [
  { labelKey: 'dialog:schedule.manual', value: null },
  { labelKey: 'dialog:schedule.hourly', value: '0 * * * *' },
  { labelKey: 'dialog:schedule.daily0200', value: '0 2 * * *' },
  { labelKey: 'dialog:schedule.daily0600', value: '0 6 * * *' },
  { labelKey: 'dialog:schedule.weekday0700', value: '0 7 * * 1-5' },
  { labelKey: 'dialog:schedule.monday0300', value: '0 3 * * 1' },
  { labelKey: 'dialog:schedule.monthly0100', value: '0 1 1 * *' },
];

/** 이 빌드가 아는 최상위 설정 키 — 그 밖의 키는 손대지 않고 그대로 실어 나른다 */
const KNOWN_KEYS = ['schedule', 'startDate', 'catchup', 'retries', 'retryDelayMin', 'timeoutMin', 'notify', 'staging'];

/**
 * 저장된 설정을 이 빌드가 쓰는 형태로 — 결측·타입 오류·범위 밖 값을 기본값으로 채운다.
 *
 * **모르는 키는 버리지 않고 그대로 보존한다.** 예전에는 아는 키만 골라 새 객체를 만들었는데, 새 빌드가 저장한
 * 워크플로를 예전 빌드로 열어 다시 저장하면 새 설정이 조용히 사라졌다 — 스탠드얼론은 현장마다 빌드가 달라
 * 실제로 일어나는 일이다. notify·staging 안쪽도 같은 이유로 모르는 키를 남긴다.
 */
export function normalizeSettings(s: any): WorkflowSettings {
  const d = DEFAULT_WORKFLOW_SETTINGS;
  if (!s || typeof s !== 'object') return { ...d, notify: { ...d.notify }, staging: { ...d.staging } };
  const passthrough = Object.fromEntries(Object.entries(s).filter(([k]) => !KNOWN_KEYS.includes(k)));
  const keep = (v: any, known: string[]) =>
    (v && typeof v === 'object') ? Object.fromEntries(Object.entries(v).filter(([k]) => !known.includes(k))) : {};
  return {
    ...passthrough,
    schedule: s.schedule ? String(s.schedule) : null,
    startDate: typeof s.startDate === 'string' && s.startDate ? s.startDate : d.startDate,
    catchup: !!s.catchup,
    retries: Number.isFinite(Number(s.retries)) ? Math.max(0, Number(s.retries)) : d.retries,
    retryDelayMin: Number.isFinite(Number(s.retryDelayMin)) ? Math.max(0, Number(s.retryDelayMin)) : d.retryDelayMin,
    timeoutMin: s.timeoutMin == null || s.timeoutMin === '' ? null : Math.max(1, Number(s.timeoutMin)),
    notify: {
      ...keep(s.notify, ['onFailure', 'onSuccess', 'emails', 'webhook']),
      onFailure: !!s.notify?.onFailure, onSuccess: !!s.notify?.onSuccess,
      emails: String(s.notify?.emails || ''), webhook: String(s.notify?.webhook || ''),
    },
    staging: {
      ...keep(s.staging, ['mode', 'thresholdRows', 'connection', 'keepHours']),
      mode: (['auto', 'file', 'table', 'xcom'] as const).includes(s.staging?.mode) ? s.staging.mode : d.staging.mode,
      thresholdRows: Number.isFinite(Number(s.staging?.thresholdRows)) && Number(s.staging?.thresholdRows) > 0 ? Number(s.staging.thresholdRows) : d.staging.thresholdRows,
      connection: String(s.staging?.connection || ''),
      keepHours: Number.isFinite(Number(s.staging?.keepHours)) && Number(s.staging?.keepHours) > 0 ? Number(s.staging.keepHours) : d.staging.keepHours,
    },
  };
}

/** 스케줄 값을 사람이 읽는 문구로 */
export function describeSchedule(schedule: string | null): string {
  if (!schedule) return i18n.t('dialog:schedule.manual');
  const p = SCHEDULE_PRESETS.find((x) => x.value === schedule);
  return p ? `${i18n.t(p.labelKey)} (${schedule})` : schedule;
}

/**
 * 실행할 때 **값을 물어야 하는** ${이름} 참조 — 실행 파라미터 입력 팝업에 쓴다.
 *
 * 노드 속성의 파라미터 표에 이미 값을 적어 둔 이름은 묻지 않는다. 치환 순서가 실행 conf > 노드 파라미터 >
 * 전역변수라 그 값만으로 실행이 되는데도 팝업이 떠서 다시 입력하라고 요구하면, 속성에 적어 둔 값이
 * 무시되는 것처럼 보인다. 값이 비어 있는 파라미터는 아직 정해지지 않은 것이므로 그대로 묻는다.
 * 노드를 감싼 서브플로에 값을 적어 둔 이름도 같다 — 실행 때 그 값으로 치환된다.
 */
export function extractParamRefs(nodes: any[], settings?: { params?: { name: string; value: string }[] } | null): string[] {
  const found = new Set<string>();
  /** 워크플로 변수에 값을 적어 둔 이름 — 실행 때 묻지 않는다 */
  const wfVars = new Set((settings?.params || []).filter((p) => p && p.name && String(p.value ?? '').trim() !== '').map((p) => p.name));
  const byId = new Map<string, any>(nodes.map((n) => [n?.id, n]));
  /** 이 노드를 감싼 서브플로들이 값을 정해 둔 이름 */
  const scoped = (n: any): Set<string> => {
    const out = new Set<string>();
    const seen = new Set<string>();
    for (let p = n?.parentId; p && byId.has(p) && !seen.has(p); p = byId.get(p)?.parentId) {
      seen.add(p);
      const g = byId.get(p);
      if (g?.type !== 'subflow') continue;
      for (const q of (g.data?.config?.params || [])) {
        if (q && typeof q.name === 'string' && String(q.value ?? '').trim() !== '') out.add(q.name);
      }
    }
    return out;
  };
  for (const n of nodes) {
    const cfg = n?.data?.config;
    const mine = new Set<string>();
    const walk = (v: any) => {
      if (typeof v === 'string') { const re = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g; let m; while ((m = re.exec(v))) mine.add(m[1]); }
      else if (Array.isArray(v)) v.forEach(walk);
      else if (v && typeof v === 'object') Object.values(v).forEach(walk);
    };
    walk(cfg);
    // 이 노드가 스스로 값을 정해 둔 이름은 뺀다 (값 안의 ${…} 는 아직 참조이므로 위에서 이미 모였다)
    for (const p of (cfg?.params || [])) {
      if (p && typeof p.name === 'string' && String(p.value ?? '').trim() !== '') mine.delete(p.name);
    }
    scoped(n).forEach((x) => mine.delete(x));
    wfVars.forEach((x) => mine.delete(x));
    mine.forEach((x) => found.add(x));
  }
  return Array.from(found).sort();
}
